import {
  hashSeed,
  mapFromRows,
  type ArenaMap,
  type Seed,
} from "../../game-core/src/index";
import { CAMPAIGN_MAPS } from "./campaign-maps";
import type {
  CampaignDefinition,
  CampaignValidationReport,
  ChallengeDefinition,
  CircuitDefinition,
  MapManifest,
  MapValidationReport,
  TournamentDefinition,
} from "./content-types";
import { validateMapManifest } from "./validation";

export * from "./campaign-maps";
export * from "./content-types";
export * from "./validation";

export interface ArenaDefinition {
  readonly id: string;
  readonly name: string;
  readonly circuit: string;
  readonly tagline: string;
  readonly rows: readonly string[];
  readonly accent: "coral" | "cyan" | "amber";
  readonly hazard: "none" | "ring" | "crossfire";
}

const AFTERGLOW: ArenaDefinition = Object.freeze({
  id: "afterglow-01",
  name: "Afterglow",
  circuit: "Circuit 01",
  tagline: "Every corner remembers the last blast.",
  accent: "coral",
  hazard: "none",
  rows: Object.freeze([
    "#############",
    "#..+....+...#",
    "#.#.#.#.#.#.#",
    "#....+...+..#",
    "#.#.#.#.#.#.#",
    "#.+...+...+.#",
    "#.#.#.#.#.#.#",
    "#..+....+...#",
    "#.#.#.#.#.#.#",
    "#.+...+...+.#",
    "#############",
  ]),
});

const NEON_RAIL: ArenaDefinition = Object.freeze({
  id: "neon-rail-02",
  name: "Neon Rail",
  circuit: "Circuit 02",
  tagline: "The safe lane is never the same lane twice.",
  accent: "cyan",
  hazard: "crossfire",
  rows: Object.freeze([
    "#############",
    "#..+....+...#",
    "#.#.#.#.#.#.#",
    "#.+..+..+..+#",
    "#.#.#.#.#.#.#",
    "#..+.....+..#",
    "#.#.#.#.#.#.#",
    "#.+..+..+..+#",
    "#.#.#.#.#.#.#",
    "#..+....+...#",
    "#############",
  ]),
});

const SUNSET_GRID: ArenaDefinition = Object.freeze({
  id: "sunset-grid-03",
  name: "Sunset Grid",
  circuit: "Circuit 03",
  tagline: "Long sightlines. Short fuses.",
  accent: "amber",
  hazard: "ring",
  rows: Object.freeze([
    "#############",
    "#.+...+...+.#",
    "#.#.#.#.#.#.#",
    "#...+...+...#",
    "#.#.#.#.#.#.#",
    "#.+...+...+.#",
    "#.#.#.#.#.#.#",
    "#...+...+...#",
    "#.#.#.#.#.#.#",
    "#.+...+...+.#",
    "#############",
  ]),
});

export const BATTLE_ARENAS: readonly ArenaDefinition[] = Object.freeze([
  AFTERGLOW,
  NEON_RAIL,
  SUNSET_GRID,
]);

export function arenaMap(definition: {
  readonly rows: readonly string[];
}): ArenaMap {
  return mapFromRows(definition.rows);
}

export function chooseBattleArena(seed: Seed): ArenaDefinition {
  const index = hashSeed(seed) % BATTLE_ARENAS.length;
  return BATTLE_ARENAS[index] ?? AFTERGLOW;
}

export interface CharacterDefinition {
  readonly id: string;
  readonly name: string;
  readonly callSign: string;
  readonly accent: "coral" | "cyan" | "amber" | "violet";
  readonly bio: string;
}

export const CHARACTERS: readonly CharacterDefinition[] = Object.freeze([
  Object.freeze({
    id: "flare",
    name: "Flare",
    callSign: "THE SPARK",
    accent: "coral",
    bio: "Moves first. Thinks in exits.",
  }),
  Object.freeze({
    id: "nova",
    name: "Nova",
    callSign: "THE ORBIT",
    accent: "cyan",
    bio: "Sees the chain before the first fuse.",
  }),
  Object.freeze({
    id: "sol",
    name: "Sol",
    callSign: "THE BREAKER",
    accent: "amber",
    bio: "Turns a blocked lane into a route.",
  }),
  Object.freeze({
    id: "echo",
    name: "Echo",
    callSign: "THE MIRROR",
    accent: "violet",
    bio: "Waits exactly one beat longer.",
  }),
]);

interface CircuitSpec {
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly accent: "coral" | "cyan" | "amber" | "violet" | "lime";
}

const CIRCUIT_SPECS: readonly CircuitSpec[] = Object.freeze([
  {
    id: "prismwake",
    name: "Prismwake",
    tagline: "Learn the language of light before it starts to move.",
    accent: "cyan",
  },
  {
    id: "copper-canopy",
    name: "Copper Canopy",
    tagline: "A warm maze of rivets, rain, and rising pressure.",
    accent: "amber",
  },
  {
    id: "moonlit-foundry",
    name: "Moonlit Foundry",
    tagline: "Where cooling metal leaves no room for a lazy route.",
    accent: "violet",
  },
  {
    id: "tidal-relay",
    name: "Tidal Relay",
    tagline: "Carry the signal through a city built on moving water.",
    accent: "coral",
  },
  {
    id: "skyline-static",
    name: "Skyline Static",
    tagline: "Restore the skyline before the storm finds its rhythm.",
    accent: "lime",
  },
]);

function defineCircuit(spec: CircuitSpec): CircuitDefinition {
  const maps = CAMPAIGN_MAPS.filter((map) => map.circuitId === spec.id);
  const finalMap = maps[maps.length - 1];
  return Object.freeze({
    id: spec.id,
    name: spec.name,
    tagline: spec.tagline,
    accent: spec.accent,
    stages: Object.freeze(
      maps.map((map) =>
        Object.freeze({
          stage: map.stage,
          mapId: map.id,
        }),
      ),
    ),
    bossMapId: finalMap?.id ?? "",
  });
}

export const CAMPAIGN_CIRCUITS: readonly CircuitDefinition[] = Object.freeze(
  CIRCUIT_SPECS.map(defineCircuit),
);

export const CAMPAIGN: CampaignDefinition = Object.freeze({
  id: "neon-fuse-campaign",
  name: "The Luminous Run",
  tagline: "Five circuits. Forty routes. One city waking up.",
  contentVersion: "neon-fuse-content-1",
  circuits: CAMPAIGN_CIRCUITS,
  maps: CAMPAIGN_MAPS,
});

export const CAMPAIGN_DEFINITION = CAMPAIGN;

export function getCampaignMap(mapId: string): MapManifest | undefined {
  return CAMPAIGN_MAPS.find((map) => map.id === mapId);
}

export function getCampaignStage(
  circuitId: string,
  stage: number,
): MapManifest | undefined {
  return CAMPAIGN_MAPS.find(
    (map) => map.circuitId === circuitId && map.stage === stage,
  );
}

export function campaignMap(definition: MapManifest): ArenaMap {
  return arenaMap(definition);
}

export const mapForManifest = campaignMap;
export const mapFromManifest = campaignMap;

export const CHALLENGES: readonly ChallengeDefinition[] = Object.freeze([
  Object.freeze({
    id: "flashpoint-sprint",
    name: "Flashpoint Sprint",
    mode: "time-attack",
    description:
      "Open the beacon lane and reach the far signal before the timer turns.",
    mapId: "prismwake-03",
    objective: "breakthrough",
    difficulty: 2,
    seed: "flashpoint-sprint-01",
    target: 900,
  }),
  Object.freeze({
    id: "last-lantern",
    name: "Last Lantern",
    mode: "survival",
    description:
      "Stay lit through three hazard cycles with no spare route wasted.",
    mapId: "copper-canopy-06",
    objective: "survive",
    difficulty: 4,
    seed: "last-lantern-01",
    target: 3,
  }),
  Object.freeze({
    id: "lockstep",
    name: "Lockstep",
    mode: "puzzle",
    description:
      "Clear a narrow crossing using the fewest deliberate placements.",
    mapId: "moonlit-foundry-04",
    objective: "control",
    difficulty: 3,
    seed: "lockstep-01",
    target: 5,
  }),
  Object.freeze({
    id: "clean-break",
    name: "Clean Break",
    mode: "precision",
    description: "Break only the marked route and keep every corner safe.",
    mapId: "tidal-relay-02",
    objective: "precision",
    difficulty: 3,
    seed: "clean-break-01",
    target: 12,
  }),
  Object.freeze({
    id: "signal-drift",
    name: "Signal Drift",
    mode: "daily-seed",
    description:
      "A seeded skyline route changes its pressure without changing its rules.",
    mapId: "skyline-static-07",
    objective: "relay",
    difficulty: 5,
    seed: "signal-drift-daily",
    target: 720,
  }),
]);

export const CHALLENGE_DEFINITIONS = CHALLENGES;

export const TOURNAMENTS: readonly TournamentDefinition[] = Object.freeze([
  Object.freeze({
    id: "afterlight-cup",
    name: "Afterlight Cup",
    format: "single-elimination",
    minPlayers: 2,
    maxPlayers: 8,
    rounds: Object.freeze([
      Object.freeze({
        round: 1,
        mapPool: Object.freeze(["prismwake-04", "copper-canopy-03"]),
      }),
      Object.freeze({
        round: 2,
        mapPool: Object.freeze(["moonlit-foundry-05", "tidal-relay-04"]),
      }),
      Object.freeze({
        round: 3,
        mapPool: Object.freeze(["skyline-static-08"]),
      }),
    ]),
  }),
  Object.freeze({
    id: "relay-circuit-open",
    name: "Relay Circuit Open",
    format: "round-robin",
    minPlayers: 4,
    maxPlayers: 8,
    rounds: Object.freeze([
      Object.freeze({
        round: 1,
        mapPool: Object.freeze(["prismwake-06", "moonlit-foundry-02"]),
      }),
      Object.freeze({
        round: 2,
        mapPool: Object.freeze(["copper-canopy-05", "tidal-relay-06"]),
      }),
      Object.freeze({
        round: 3,
        mapPool: Object.freeze(["skyline-static-06", "skyline-static-07"]),
      }),
    ]),
  }),
]);

export const TOURNAMENT_DEFINITIONS = TOURNAMENTS;

export function validateCampaignMaps(
  maps: readonly MapManifest[] = CAMPAIGN_MAPS,
): readonly MapValidationReport[] {
  return Object.freeze(maps.map(validateMapManifest));
}

export function validateCampaignContent(
  campaign: CampaignDefinition = CAMPAIGN,
): CampaignValidationReport {
  const maps = validateCampaignMaps(campaign.maps);
  const errors: string[] = [];

  if (campaign.circuits.length !== 5) {
    errors.push("campaign must contain exactly five circuits");
  }
  if (campaign.maps.length !== 40) {
    errors.push("campaign must contain exactly forty maps");
  }

  const mapIds = new Set<string>();
  for (const map of campaign.maps) {
    if (mapIds.has(map.id)) {
      errors.push(`duplicate map id: ${map.id}`);
    }
    mapIds.add(map.id);
  }

  for (const report of maps) {
    if (!report.valid) {
      errors.push(...report.errors.map((error) => `${report.mapId}: ${error}`));
    }
  }

  for (const circuit of campaign.circuits) {
    if (circuit.stages.length !== 8) {
      errors.push(`${circuit.id} must contain exactly eight stages`);
    }
    for (const stage of circuit.stages) {
      const map = campaign.maps.find(
        (candidate) => candidate.id === stage.mapId,
      );
      if (map === undefined) {
        errors.push(`${circuit.id} references missing map ${stage.mapId}`);
      } else if (map.circuitId !== circuit.id || map.stage !== stage.stage) {
        errors.push(`${circuit.id} has a mismatched stage ${stage.stage}`);
      }
    }
  }

  const invalidMaps = maps.filter((report) => !report.valid);
  return Object.freeze({
    valid: errors.length === 0,
    mapCount: campaign.maps.length,
    maps,
    invalidMaps: Object.freeze([...invalidMaps]),
    errors: Object.freeze(errors),
  });
}

export const validateContent = validateCampaignContent;
export const CAMPAIGN_VALIDATION = validateCampaignContent(CAMPAIGN);

export function assertValidCampaignContent(
  campaign: CampaignDefinition = CAMPAIGN,
): void {
  const report = validateCampaignContent(campaign);
  if (!report.valid) {
    throw new RangeError(report.errors.join("; "));
  }
}
